/**
 * Create Assessment Wizard
 *
 * 5-step wizard for creating assessments with AI generation:
 * 1. Basic Details (name, objective, mode)
 * 2. Source Selection (upload/guided/text)
 * 3. Content Input (based on source)
 * 4. Magic Moment (AI generates package)
 * 5. Package Review (review and publish)
 */

import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api';
import {
  WizardProgress,
  BasicDetailsStep,
  SourceSelectionStep,
  DocumentUploadStep,
  GuidedQuestionsStep,
  FreeTextStep,
  MagicMomentStep,
  WizardState,
  INITIAL_WIZARD_STATE,
  WIZARD_LIMITS,
} from '../../components/wizard';
import type { PackageSummary } from '../../lib/types';

export function CreateAssessmentWizard() {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [wizardState, setWizardState] = useState<WizardState>(INITIAL_WIZARD_STATE);
  const [stepErrors, setStepErrors] = useState<string[]>([]);

  // Assessment creation state
  const [createdAssessmentId, setCreatedAssessmentId] = useState<string | null>(null);
  const [isCreatingAssessment, setIsCreatingAssessment] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Generation result
  const [packageSummary, setPackageSummary] = useState<PackageSummary | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const handleStateChange = (updates: Partial<WizardState>) => {
    setWizardState((prev) => ({ ...prev, ...updates }));
    setStepErrors([]);

    // Invalidate completed steps when critical fields change
    const invalidatedSteps = new Set<number>();

    if ('name' in updates || 'assessmentMode' in updates) {
      invalidatedSteps.add(1);
    }

    if ('sourceType' in updates) {
      invalidatedSteps.add(2);
      invalidatedSteps.add(3);
    }

    if ('documentText' in updates || 'additionalContext' in updates || 'guidedAnswers' in updates) {
      invalidatedSteps.add(3);
    }

    if (invalidatedSteps.size > 0) {
      setCompletedSteps((prev) => {
        const newSet = new Set(prev);
        invalidatedSteps.forEach((step) => newSet.delete(step));
        return newSet;
      });
      // Reset assessment creation if going back
      setCreatedAssessmentId(null);
      setPackageSummary(null);
    }
  };

  // Validation functions
  const validateStep1 = (): string[] => {
    const errors: string[] = [];
    const { name, objective } = wizardState;

    if (!name.trim()) {
      errors.push('Assessment name is required');
    }
    if (name.length > WIZARD_LIMITS.NAME_MAX) {
      errors.push(`Name must be ${WIZARD_LIMITS.NAME_MAX} characters or less`);
    }
    if (objective.length > WIZARD_LIMITS.OBJECTIVE_MAX) {
      errors.push(`Objective must be ${WIZARD_LIMITS.OBJECTIVE_MAX} characters or less`);
    }

    return errors;
  };

  const validateStep3 = (): string[] => {
    const errors: string[] = [];
    const { sourceType, documentText, additionalContext } = wizardState;

    if (sourceType === 'document' && !documentText.trim()) {
      errors.push('Please upload a document');
    }
    if (sourceType === 'freetext' && additionalContext.length > WIZARD_LIMITS.ADDITIONAL_CONTEXT_MAX) {
      errors.push(`Content must be ${WIZARD_LIMITS.ADDITIONAL_CONTEXT_MAX} characters or less`);
    }

    return errors;
  };

  const validateCurrentStep = (): boolean => {
    let errors: string[] = [];

    switch (currentStep) {
      case 1:
        errors = validateStep1();
        break;
      case 3:
        errors = validateStep3();
        break;
    }

    setStepErrors(errors);
    return errors.length === 0;
  };

  // Create assessment and move to Magic Moment step
  const createAssessmentAndAdvance = useCallback(async () => {
    setCreateError(null);
    setIsCreatingAssessment(true);

    try {
      const {
        name,
        objective,
        assessmentTiming,
        assessmentMode,
        sourceType,
        documentText,
        guidedAnswers,
        additionalContext,
        showScoreToLearner,
        selectedRubricId,
        externalReferenceId,
        sourceSystem,
      } = wizardState;

      // Build metadata based on source type
      const metadata: Record<string, unknown> = {
        assessmentMode,
        showScoreToLearner,
      };

      if (sourceType === 'document' && documentText.trim()) {
        metadata.documentText = documentText;
      } else if (sourceType === 'guided' && Object.keys(guidedAnswers).length > 0) {
        const filteredAnswers = Object.fromEntries(
          Object.entries(guidedAnswers).filter(([, v]) => v?.trim())
        );
        if (Object.keys(filteredAnswers).length > 0) {
          metadata.guidedAnswers = filteredAnswers;
        }
      } else if (sourceType === 'freetext' && additionalContext.trim()) {
        metadata.additionalContext = additionalContext.trim();
      }

      const res = await apiClient.createAssessment({
        name: name.trim(),
        objective: objective.trim(),
        assessmentType: assessmentTiming,
        assessmentMode,
        rubricVersion: selectedRubricId || undefined,
        externalReferenceId: externalReferenceId.trim() || undefined,
        sourceSystem: sourceSystem.trim() || undefined,
        metadata,
      });

      if (res.response.data) {
        setCreatedAssessmentId(res.response.data.id);
        setCompletedSteps((prev) => new Set([...prev, 3]));
        setCurrentStep(4);
      } else if (res.response.error) {
        setCreateError(`Failed to create assessment: ${res.response.error.message}`);
      }
    } catch (error) {
      setCreateError(
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    } finally {
      setIsCreatingAssessment(false);
    }
  }, [wizardState]);

  const handleNext = () => {
    if (!validateCurrentStep()) return;

    setCompletedSteps((prev) => new Set([...prev, currentStep]));

    // Step 3 -> Step 4: Create assessment first
    if (currentStep === 3) {
      createAssessmentAndAdvance();
      return;
    }

    setCurrentStep((prev) => Math.min(5, prev + 1));
  };

  const handleBack = () => {
    setStepErrors([]);
    setCreateError(null);

    // If going back from step 4 or 5, reset the assessment creation
    if (currentStep >= 4) {
      setCreatedAssessmentId(null);
      setPackageSummary(null);
      setGenerationError(null);
    }

    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const handleStepClick = (step: number) => {
    // Can only go back to completed steps, not forward
    if (step < currentStep && completedSteps.has(step)) {
      setStepErrors([]);
      setCurrentStep(step);
      // Reset assessment if going before step 4
      if (step < 4) {
        setCreatedAssessmentId(null);
        setPackageSummary(null);
        setGenerationError(null);
      }
    }
  };

  // Generation callbacks
  const handleGenerationComplete = (summary: PackageSummary) => {
    setPackageSummary(summary);
    setCompletedSteps((prev) => new Set([...prev, 4]));
    setCurrentStep(5);
  };

  const handleGenerationError = (error: string) => {
    setGenerationError(error);
  };

  const handleRetryGeneration = () => {
    setGenerationError(null);
  };

  // Render content for step 3 based on source type
  const renderStep3Content = () => {
    switch (wizardState.sourceType) {
      case 'document':
        return (
          <DocumentUploadStep
            state={wizardState}
            onChange={handleStateChange}
            errors={stepErrors}
          />
        );
      case 'guided':
        return (
          <GuidedQuestionsStep
            state={wizardState}
            onChange={handleStateChange}
            errors={stepErrors}
            trainingName={wizardState.name}
            objective={wizardState.objective}
          />
        );
      case 'freetext':
        return (
          <FreeTextStep
            state={wizardState}
            onChange={handleStateChange}
            errors={stepErrors}
          />
        );
    }
  };

  // Render step 5: Package Review (simplified for now)
  const renderPackageReview = () => {
    if (!createdAssessmentId || !packageSummary) {
      return (
        <div>
          <h2>Package Review</h2>
          <p className="text-muted">Loading package details...</p>
        </div>
      );
    }

    return (
      <div>
        <h2 style={{ marginBottom: 8 }}>Your Assessment is Ready!</h2>
        <p className="text-muted" style={{ marginBottom: 24 }}>
          AI has created your assessment package. Review the details below.
        </p>

        <div
          style={{
            padding: 20,
            background: 'var(--semantic-success-light)',
            borderRadius: 8,
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Assessment Name
              </div>
              <div style={{ fontWeight: 600 }}>{wizardState.name}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Mode
              </div>
              <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                {wizardState.assessmentMode}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Competencies
              </div>
              <div style={{ fontWeight: 600 }}>{packageSummary.competencyCount}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Scenario
              </div>
              <div style={{ fontWeight: 600 }}>{packageSummary.scenarioTitle}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                AI Persona
              </div>
              <div style={{ fontWeight: 600 }}>{packageSummary.personaName}</div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate(`/console/trainer/assessments/${createdAssessmentId}?created=true`)}
          >
            View Full Assessment
          </button>
          <Link to="/console/trainer/assessments" className="btn btn-secondary">
            Back to Assessments
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <Link to="/console/trainer/assessments" style={{ color: 'var(--text-tertiary)' }}>
          &larr; Back to Assessments
        </Link>
      </div>

      <h1>Create Assessment</h1>

      <WizardProgress
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={handleStepClick}
      />

      {createError && (
        <div className="error-box" style={{ marginBottom: 16 }}>
          {createError}
        </div>
      )}

      {generationError && currentStep === 4 && (
        <div className="error-box" style={{ marginBottom: 16 }}>
          {generationError}
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        {currentStep === 1 && (
          <BasicDetailsStep
            state={wizardState}
            onChange={handleStateChange}
            errors={stepErrors}
          />
        )}

        {currentStep === 2 && (
          <SourceSelectionStep state={wizardState} onChange={handleStateChange} />
        )}

        {currentStep === 3 && renderStep3Content()}

        {currentStep === 4 && createdAssessmentId && (
          <MagicMomentStep
            assessmentId={createdAssessmentId}
            onComplete={handleGenerationComplete}
            onError={handleGenerationError}
            onRetry={handleRetryGeneration}
          />
        )}

        {currentStep === 5 && renderPackageReview()}
      </div>

      {/* Navigation buttons - only show for steps 1-3 */}
      {currentStep < 4 && (
        <div className="card">
          <div style={{ display: 'flex', gap: 12 }}>
            {currentStep > 1 && (
              <button type="button" className="btn btn-secondary" onClick={handleBack}>
                Back
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleNext}
              disabled={isCreatingAssessment}
            >
              {isCreatingAssessment
                ? 'Creating...'
                : currentStep === 3
                  ? 'Generate Assessment'
                  : 'Continue'}
            </button>
            <Link to="/console/trainer/assessments" className="btn btn-secondary">
              Cancel
            </Link>
          </div>
        </div>
      )}

      {/* Step 4: Show Back/Cancel if there's any error or missing assessment ID */}
      {currentStep === 4 && (generationError || !createdAssessmentId) && (
        <div className="card">
          <div style={{ display: 'flex', gap: 12 }}>
            <button type="button" className="btn btn-secondary" onClick={handleBack}>
              Back
            </button>
            <Link to="/console/trainer/assessments" className="btn btn-secondary">
              Cancel
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
